import React, { useState } from 'react';
import {
  Modal,
  Box,
  TextField,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Typography,
  IconButton,
  CircularProgress,
  Divider,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useFormik } from 'formik';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../firebaseConfig';
import { addNewItem, updateShelfPackageDetails, updateShelfShoppingPackageDetails } from '../../helper/functions';
import { toast } from 'react-toastify';
import { useSelector, useDispatch } from 'react-redux';
import { setSelectedPackage } from '../../store/commonSlice';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { serverTimestamp } from 'firebase/firestore';
import { calculateTotalValues } from '../../pages/Shipment/shippingRequest/helper';

const initialValues = {
  itemLink: '',
  itemImages: [], // Change to an array for multiple images
  itemName: '',
  valuePerItem: '',
  quantity: '',
  weightPerItem: '',
  dimensions: '',
  purchasedBefore: '',
  needAssistance: '',
  receiptFile: null,
  itemCondition: '',
};

const AddItemModal = ({ open, toggleModel,isShoppingItem }) => {
  const { selectedPackage: packageActiveData } = useSelector((state) => state.common);
  const { value: currentUser } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedImageFiles, setSelectedImageFiles] = useState([]);
  const [selectedReceiptFile, setSelectedReceiptFile] = useState(null);

  const formik = useFormik({
    initialValues,
    onSubmit: async (values, { resetForm }) => {
      setIsSubmitting(true);
      try {
        // Upload all images to Firebase Storage and get their URLs
        const uploadedImageUrls = await Promise.all(
          values.itemImages.map(async (file) => {
            const storageRef = ref(storage, `images/${Date.now()}_${file.name}`);
            await uploadBytes(storageRef, file);
            return getDownloadURL(storageRef);
          })
        );

        // Upload receipt file if it exists
        let receiptDocUrls = [];
        if (values.receiptFile) {
          const receiptStorageRef = ref(storage, `receipts/${Date.now()}_${values.receiptFile.name}`);
          await uploadBytes(receiptStorageRef, values.receiptFile);
          const receiptUrl = await getDownloadURL(receiptStorageRef);
          receiptDocUrls = [receiptUrl];
        }

        // Prepare item data
        const itemData = {
          ...values,
          itemImages: uploadedImageUrls, // Store URLs instead of File objects
          receiptDocUrls: receiptDocUrls, // Store receipt URLs
        };

        // Add item to Firestore
        const itemId = await addNewItem(itemData, packageActiveData,isShoppingItem);
        console.log(packageActiveData, "packageActiveData");

        // Update the shelf package with the new item
        if (packageActiveData?.id && !isShoppingItem) {
          // First, update package with new item ID and images
          const updatedPackageItemIds = [...(packageActiveData.packageItemIds || []), itemId];
          const updatedPackageImages = [...(packageActiveData.packageImages || []), ...uploadedImageUrls];
          
          await updateShelfPackageDetails(packageActiveData.id, {
            packageItemIds: updatedPackageItemIds,
            packageImages: updatedPackageImages,
            updatedAt: serverTimestamp(),
          });
          
          console.log('Package updated with new item');
          
          // Now calculate total values using the existing function
          const calculatedTotals = await calculateTotalValues([packageActiveData.id]);
          
          if (calculatedTotals) {
            // Update package with calculated totals
            await updateShelfPackageDetails(packageActiveData.id, {
              packageTotalAmount: calculatedTotals.totalAmount,
              packageTotalWeight: calculatedTotals.totalWeight,
              updatedAt: serverTimestamp(),
            });
            
            // Update Redux state directly with calculated data
            const updatedPackageData = {
              ...packageActiveData,
              packageItemIds: updatedPackageItemIds,
              packageImages: updatedPackageImages,
              packageTotalAmount: calculatedTotals.totalAmount,
              packageTotalWeight: calculatedTotals.totalWeight,
              updatedAt: new Date(),
            };
            
            // Update Redux state with new package data
            dispatch(setSelectedPackage(updatedPackageData));
            console.log('Redux state updated with new package data');
          }
        }
        else if(isShoppingItem){
          const updatedPackageItemIds = [...(packageActiveData.shoppingItemIds || []), itemId];
          const updatedPackageImages = [...(packageActiveData.shoppingImages || []), ...uploadedImageUrls];
          const itemDetails = [...(packageActiveData.itemDetails || []), {
            brandLogo:"",
            brandName:"",
            itemId:itemId,
            itemImageUrl:uploadedImageUrls[0],
            itemName:values.itemName,
            itemPrice:values.valuePerItem,
            itemQty:values.quantity,
            itemStatus:"PENDING",
            itemWeight:values.weightPerItem,
            orderedAt:null,
            receivedAt:null,
            trackingNumber:""
          }];
          await updateShelfShoppingPackageDetails(packageActiveData.id, {
            itemDetails: itemDetails,
            shoppingItemIds: updatedPackageItemIds,
            shoppingImages: updatedPackageImages,
            totalItemPrice: packageActiveData.totalItemPrice + (+values.valuePerItem * +values.quantity),
            totalItemWeight: packageActiveData.totalItemWeight + (+values.weightPerItem * +values.quantity),
          });
          const updatedPackageData = {
            ...packageActiveData,
            shoppingItemIds: updatedPackageItemIds,
            shoppingImages: updatedPackageImages,
            itemDetails: itemDetails,
            totalItemPrice: packageActiveData.totalItemPrice + (+values.valuePerItem * +values.quantity),
            totalItemWeight: packageActiveData.totalItemWeight + (+values.weightPerItem * +values.quantity),
          };
          dispatch(setSelectedPackage(updatedPackageData));
        }

        // Success handling
        toast.success('Item added successfully!');
        resetForm();
        setSelectedImageFiles([]);
        setSelectedReceiptFile(null);
        toggleModel();
      } catch (error) {
        console.error('Error adding item:', error);
        toast.error('Error adding item: ' + error.message);
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const handleFileChange = (event) => {
    const files = Array.from(event.currentTarget.files);
    formik.setFieldValue('itemImages', files);
    setSelectedImageFiles(files);
  };

  const handleReceiptFileChange = (event) => {
    const file = event.currentTarget.files[0];
    formik.setFieldValue('receiptFile', file);
    setSelectedReceiptFile(file);
  };

  const handleClose = () => {
    if (!isSubmitting) {
      formik.resetForm();
      setSelectedImageFiles([]);
      setSelectedReceiptFile(null);
      toggleModel();
    }
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={styles.modalBox}>
        {/* Header with background color */}
        <Box sx={styles.header}>
          <Typography variant="h5" fontWeight={600} sx={styles.title}>
            Add New Item
          </Typography>
          <IconButton 
            onClick={handleClose} 
            size="large"
            disabled={isSubmitting}
            sx={styles.closeButton}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Form */}
        <Box component="form" onSubmit={formik.handleSubmit} sx={styles.formBox}>
          {/* Item Link */}
          <TextField
            label="Item Link"
            name="itemLink"
            value={formik.values.itemLink}
            onChange={formik.handleChange}
            fullWidth
            margin="normal"
            placeholder="https://example.com/product"
            disabled={isSubmitting}
          />

          {/* Item Images Upload */}
          <Box sx={styles.uploadSection}>
            <Button
              variant="outlined"
              component="label"
              fullWidth
              startIcon={<CloudUploadIcon />}
              disabled={isSubmitting}
              sx={styles.uploadButton}
            >
              {selectedImageFiles.length > 0 
                ? `${selectedImageFiles.length} image(s) selected`
                : 'Upload Item Images'
              }
              <input
                type="file"
                hidden
                multiple
                accept="image/*"
                onChange={handleFileChange}
              />
            </Button>
            {selectedImageFiles.length > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                {selectedImageFiles.map(file => file.name).join(', ')}
              </Typography>
            )}
          </Box>

          {/* Item Name */}
          <TextField
            label="Item Name"
            name="itemName"
            value={formik.values.itemName}
            onChange={formik.handleChange}
            fullWidth
            margin="normal"
            placeholder="Enter item name"
            disabled={isSubmitting}
            required
          />

          {/* Value and Quantity */}
          <Box sx={styles.row}>
            <TextField
              label="Value per Item"
              name="valuePerItem"
              value={formik.values.valuePerItem}
              onChange={formik.handleChange}
              fullWidth
              margin="normal"
              type="number"
              placeholder="0.00"
              disabled={isSubmitting}
              required
            />
            <TextField
              label="Quantity"
              name="quantity"
              value={formik.values.quantity}
              onChange={formik.handleChange}
              fullWidth
              margin="normal"
              type="number"
              placeholder="1"
              disabled={isSubmitting}
              required
            />
          </Box>

          {/* Weight and Dimensions */}
          <Box sx={styles.row}>
            <TextField
              label="Weight per Item (kg)"
              name="weightPerItem"
              value={formik.values.weightPerItem}
              onChange={formik.handleChange}
              fullWidth
              margin="normal"
              type="number"
              placeholder="0.00"
              disabled={isSubmitting}
              required
            />
            <TextField
              label="Dimensions"
              name="dimensions"
              value={formik.values.dimensions}
              onChange={formik.handleChange}
              fullWidth
              margin="normal"
              placeholder="L x W x H (cm)"
              disabled={isSubmitting}
            />
          </Box>

          {/* Purchase Status */}
          <Box sx={styles.section}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
              Purchase Status
            </Typography>
            <RadioGroup
              name="purchasedBefore"
              value={formik.values.purchasedBefore}
              onChange={formik.handleChange}
              row
              sx={{ mb: 2 }}
            >
              <FormControlLabel 
                value="yes_receipt" 
                control={<Radio disabled={isSubmitting} />} 
                label="Yes, has receipt" 
              />
              <FormControlLabel 
                value="yes_no_receipt" 
                control={<Radio disabled={isSubmitting} />} 
                label="Yes, no receipt" 
              />
              <FormControlLabel 
                value="no" 
                control={<Radio disabled={isSubmitting} />} 
                label="Not yet" 
              />
            </RadioGroup>

            {/* Conditional receipt upload */}
            {formik.values.purchasedBefore === 'yes_receipt' && (
              <Box sx={styles.uploadSection}>
                <Button
                  variant="outlined"
                  component="label"
                  fullWidth
                  startIcon={<CloudUploadIcon />}
                  disabled={isSubmitting}
                  sx={styles.uploadButton}
                >
                  {selectedReceiptFile ? selectedReceiptFile.name : 'Upload Receipt'}
                  <input
                    type="file"
                    hidden
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                    onChange={handleReceiptFileChange}
                  />
                </Button>
              </Box>
            )}

            {/* Conditional assistance question */}
            {formik.values.purchasedBefore === 'no' && (
              <Box sx={styles.section}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                  Customer Assistance
                </Typography>
                <RadioGroup
                  name="needAssistance"
                  value={formik.values.needAssistance}
                  onChange={formik.handleChange}
                  row
                >
                  <FormControlLabel 
                    value="yes" 
                    control={<Radio disabled={isSubmitting} />} 
                    label="Yes, needs assistance" 
                  />
                  <FormControlLabel 
                    value="no" 
                    control={<Radio disabled={isSubmitting} />} 
                    label="No assistance needed" 
                  />
                </RadioGroup>
              </Box>
            )}
          </Box>

          {/* Item Condition */}
          <FormControl fullWidth margin="normal">
            <InputLabel>Item Condition</InputLabel>
            <Select
              name="itemCondition"
              value={formik.values.itemCondition}
              onChange={formik.handleChange}
              label="Item Condition"
              disabled={isSubmitting}
              required
            >
              <MenuItem value="">Select condition</MenuItem>
              <MenuItem value="new">New</MenuItem>
              <MenuItem value="like_new">Like New</MenuItem>
              <MenuItem value="used">Used</MenuItem>
              <MenuItem value="refurbished">Refurbished</MenuItem>
              <MenuItem value="renewed">Renewed</MenuItem>
              <MenuItem value="damaged_unusable">Damaged Unusable</MenuItem>
              <MenuItem value="damaged_repairable">Damaged Repairable</MenuItem>
            </Select>
          </FormControl>

          {/* Submit Button */}
          <Button
            variant="contained"
            color="primary"
            type="submit"
            fullWidth
            disabled={isSubmitting}
            sx={styles.submitButton}
            startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {isSubmitting ? 'Adding Item...' : 'Add Item'}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

const styles = {
  modalBox: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90%',
    maxWidth: 700,
    maxHeight: '90vh',
    bgcolor: 'background.paper',
    borderRadius: 3,
    boxShadow: 24,
    overflowY: 'auto',
    p: 0,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    p: 3,
    pb: 2,
    background: 'linear-gradient(135deg, #481AA3 0%, #6B46C1 100%)',
    color: 'white',
    borderRadius: '12px 12px 0 0',
  },
  title: {
    color: 'white',
    fontSize: '1.5rem',
  },
  closeButton: {
    color: 'white',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
  },
  formBox: {
    display: 'flex',
    flexDirection: 'column',
    p: 3,
    pt: 2,
  },
  row: {
    display: 'flex',
    gap: 2,
    '@media (max-width: 600px)': {
      flexDirection: 'column',
      gap: 0,
    },
  },
  section: {
    mt: 3,
    mb: 2,
  },
  uploadSection: {
    mt: 2,
    mb: 1,
  },
  uploadButton: {
    borderColor: '#481AA3',
    color: '#481AA3',
    '&:hover': {
      borderColor: '#6B46C1',
      backgroundColor: 'rgba(72, 26, 163, 0.04)',
    },
  },
  submitButton: {
    mt: 4,
    mb: 2,
    py: 1.5,
    background: 'linear-gradient(135deg, #481AA3 0%, #6B46C1 100%)',
    '&:hover': {
      background: 'linear-gradient(135deg, #3B1466 0%, #553C9A 100%)',
    },
    '&:disabled': {
      background: '#E0E0E0',
    },
  },
};

export default AddItemModal;